// Numerology Calculator Service
export class NumerologyCalculator {
  private static charValues: { [key: string]: number } = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, I: 9,
    J: 1, K: 2, L: 3, M: 4, N: 5, O: 6, P: 7, Q: 8, R: 9,
    S: 1, T: 2, U: 3, V: 4, W: 5, X: 6, Y: 7, Z: 8
  }

  private static calculateNumber(
    fullName: string,
    charSet: string,
    validYIndexes: number[]
  ): number {
    let sum = 0

    for (let i = 0; i < fullName.length; i++) {
      const char = fullName[i].toUpperCase()

      if (char === "Y" && !validYIndexes.includes(i)) {
        continue
      }

      if (charSet.includes(char)) {
        sum += this.charValues[char] || 0
      }
    }

    return this.reduceNumber(sum)
  }

  private static reduceNumber(num: number): number {
    if (num === 11 || num === 22) return num
    while (num > 9 && num !== 11 && num !== 22) {
      num = Array.from(String(num), Number).reduce((a, b) => a + b, 0)
    }
    return num
  }

  private static isVowel(char: string): boolean {
    return "AEIOU".includes(char)
  }

  private static isConsonant(char: string): boolean {
    return "BCDFGHJKLMNPQRSTVWXYZ".includes(char)
  }

  public static calculateSoulNumber(fullName: string): number {
    let vowels = "AEIOU"
    const validYIndexes: number[] = []
    const nameParts = fullName.trim().split(" ")

    nameParts.forEach(part => {
      for (let i = 0; i < part.length; i++) {
        const currentChar = part[i].toUpperCase()
        if (currentChar === "Y") {
          const prevChar = i > 0 ? part[i - 1].toUpperCase() : ""
          const nextChar = i < part.length - 1 ? part[i + 1].toUpperCase() : ""
          const absoluteIndex = fullName.indexOf(part) + i

          if (
            (i === 0 && this.isConsonant(nextChar)) ||
            (i === part.length - 1 && this.isConsonant(prevChar)) ||
            (this.isConsonant(prevChar) && this.isConsonant(nextChar)) ||
            (this.isConsonant(prevChar) && this.isVowel(nextChar))
          ) {
            vowels += "Y"
            validYIndexes.push(absoluteIndex)
          }
        }
      }
    })

    return this.calculateNumber(fullName, vowels, validYIndexes)
  }

  public static calculateMotivationNumber(fullName: string): number {
    let consonants = "BCDFGHJKLMNPQRSTVWXZ"
    const validYIndexes: number[] = []
    const nameParts = fullName.trim().split(" ")

    nameParts.forEach(part => {
      for (let i = 0; i < part.length; i++) {
        const currentChar = part[i].toUpperCase()
        if (currentChar === "Y") {
          const prevChar = i > 0 ? part[i - 1].toUpperCase() : ""
          const nextChar = i < part.length - 1 ? part[i + 1].toUpperCase() : ""
          const absoluteIndex = fullName.indexOf(part) + i

          if (
            (i === 0 && this.isVowel(nextChar)) ||
            (i === part.length - 1 && this.isVowel(prevChar)) ||
            (this.isVowel(prevChar) && this.isVowel(nextChar)) ||
            (this.isVowel(prevChar) && this.isConsonant(nextChar))
          ) {
            consonants += "Y"
            validYIndexes.push(absoluteIndex)
          }
        }
      }
    })

    return this.calculateNumber(fullName, consonants, validYIndexes)
  }
}

// Export calculation functions
export const calculateSoulNumber = (name: string): number => {
  return NumerologyCalculator.calculateSoulNumber(name)
}

export const calculateMotivationNumber = (name: string): number => {
  return NumerologyCalculator.calculateMotivationNumber(name)
}

// Numerology Descriptions
export const getSoulDescription = (number: number): string => {
  const descriptions = {
    '1': "Sua alma é aquela que busca ser pioneira. Há dentro de você um impulso para liderar, tomar a frente e, com uma energia vibrante e criativa, marcar a diferença por onde passa. Essa necessidade de independência e a vontade de explorar novos caminhos fazem de você alguém que inspira os outros a também terem coragem de inovar e deixar sua marca no mundo.",
    '2': "Sua alma anseia por criar conexões profundas e viver em harmonia com o mundo. É como se você tivesse uma sensibilidade natural para entender as pessoas, promovendo união e paz onde vai. Sua essência se realiza ao ser um ponto de equilíbrio e entendimento, alguém que traz acolhimento e cria um ambiente onde todos se sentem respeitados e compreendidos.",
    '3': "Sua alma é pura expressão e criatividade! Existe dentro de você uma alegria contagiante, uma capacidade especial de trazer cor e leveza ao mundo. Você se realiza ao compartilhar suas ideias, iluminar os espaços ao seu redor e inspirar as pessoas a verem a vida de forma mais leve e otimista. A comunicação e a arte são como um chamado natural para você.",
    '4': "Sua alma busca estabilidade e segurança. É como se sua essência fosse enraizada em uma força prática e confiável, com um grande desejo de construir algo sólido e duradouro. A responsabilidade é parte de você, e você se realiza quando consegue organizar e estruturar, garantindo que as coisas ao seu redor tenham bases firmes.",
    '5': "Sua alma vibra pela liberdade e pela aventura. Existe em você uma curiosidade insaciável, uma energia que quer explorar o novo e vivenciar diferentes experiências. Você se sente completo ao abraçar mudanças e ao experimentar a vida de forma flexível e aberta, sempre se adaptando e evoluindo com cada nova descoberta.",
    '6': "Sua alma é feita de cuidado e de responsabilidade para com os outros. Existe em você um amor genuíno e um senso de justiça que te fazem buscar equilíbrio e paz para aqueles ao seu redor. É como se a harmonia fosse sua missão, e você se sente realizado ao criar ambientes onde todos se sentem seguros e valorizados.",
    '7': "Sua alma busca sabedoria e profundidade. Existe dentro de você uma curiosidade que vai além do superficial, uma vontade de entender o que está por trás das coisas. Você encontra paz no conhecimento e no autoconhecimento, e sua espiritualidade e reflexão fazem parte do seu caminho para se sentir completo.",
    '8': "Sua alma anseia por realização e por ver o poder da transformação no mundo. Você tem uma habilidade natural para liderar e manifestar, com uma energia que quer deixar marcas e alcançar metas significativas. A prosperidade e o sucesso material são caminhos nos quais você encontra uma grande realização pessoal.",
    '9': "Sua alma vibra com compaixão e generosidade, com um desejo sincero de fazer o bem. Existe em você uma visão de unidade, um sentimento humanitário que o impulsiona a buscar um mundo mais justo. Sua essência se realiza ao contribuir para o bem comum, trazendo harmonia e apoio onde é necessário.",
    '11': "Sua alma carrega uma sensibilidade espiritual impressionante, e sua intuição é aguçada. Você é como uma luz para os outros, guiando com sabedoria e trazendo inspiração. Seu propósito se revela ao ajudar outros a encontrarem seu próprio caminho espiritual e a elevarem suas consciências.",
    '22': "Sua alma possui o potencial de transformar e realizar grandes feitos. Você carrega uma habilidade especial para equilibrar o prático e o espiritual, manifestando projetos que deixam um impacto significativo e duradouro no mundo. Ser um construtor de grandes ideias é o que completa sua essência."
  }
  return descriptions[number.toString() as keyof typeof descriptions] || ""
}

export const getMotivationDescription = (number: number): string => {
  const descriptions = {
    '1': "Você é motivado pela independência e pela vontade de conquistar o seu espaço. Dentro de você existe uma força poderosa, um impulso que te leva a ser um exemplo de determinação e coragem. Cada passo em direção a novos caminhos é uma fonte de realização, e liderar é algo que te energiza.",
    '2': "Sua motivação vem das conexões e do trabalho em equipe. É na cooperação que você prospera, mediando e harmonizando as relações ao seu redor. Você encontra propósito ao criar um ambiente onde as pessoas se sentem acolhidas e compreendidas, sendo alguém que inspira confiança e equilíbrio.",
    '3': "Você é impulsionado pela necessidade de expressar o que sente e pensa. Existe em você um talento natural para inspirar e comunicar ideias, e isso te motiva profundamente. Trazer cor e criatividade ao mundo é uma fonte de energia para você, e isso te leva a enriquecer os ambientes ao seu redor.",
    '4': "Sua motivação surge da construção e da organização. Estruturar e consolidar são atividades que trazem propósito à sua vida, e você se realiza ao criar bases sólidas e duradouras. Para você, a estabilidade é uma grande conquista, e organizar é uma maneira de expressar o que há de mais autêntico em sua essência.",
    '5': "A liberdade e a mudança são suas grandes fontes de motivação. Você é alguém que vive para experimentar o novo e se adapta com facilidade a qualquer cenário. Aventura é um combustível para você, que se sente pleno ao explorar e aprender com cada nova experiência que a vida oferece.",
    '6': "Seu senso de responsabilidade e de cuidado com os outros é o que motiva você. Você se sente realizado ao contribuir para o bem-estar das pessoas ao seu redor, criando harmonia e apoiando quem precisa. Para você, proporcionar um ambiente acolhedor e justo é uma grande fonte de satisfação.",
    '7': "A busca por conhecimento e entendimento profundo é o que impulsiona sua energia. Você encontra propósito na exploração das verdades essenciais, e essa motivação para desvendar mistérios e conectar-se com sua espiritualidade é o que faz sua essência vibrar mais forte.",
    '8': "O sucesso e a realização material são suas maiores motivações. Você sente uma energia especial ao alcançar suas metas e construir um legado próspero. A cada conquista, sua ambição se torna mais forte, e o poder de transformar seu mundo é uma grande fonte de realização.",
    '9': "Você é movido pelo desejo de ajudar e servir à humanidade. Sua motivação vem de um impulso humanitário, que o leva a contribuir para um mundo melhor. Fazer a diferença para o bem coletivo é o que te dá energia e te faz sentir que está no caminho certo.",
    '11': "Sua motivação nasce da necessidade de elevar consciências e inspirar os outros. Existe em você uma capacidade de guiar e trazer novas perspectivas, e isso te traz realização. Ao atuar como uma luz para os outros, você sente que cumpre seu propósito mais elevado.",
    '22': "Você é motivado pela capacidade de manifestar grandes ideias e projetos de impacto. Transformar o mundo de forma prática e significativa é uma realização para você, e cada conquista que traz um impacto duradouro é o que dá propósito e força à sua jornada."
  }
  return descriptions[number.toString() as keyof typeof descriptions] || ""
}